import R from 'ramda';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { isDefined, localize } from 'common/utils';

import useVenueSelection from './useVenueSelection';
import { VenueWithSharedMenuLinkInfo } from './useVenuesWithSelectionLinks';
import { useScopeContext } from '../contexts/ScopeContext';
import { getTypedKeys } from '../utils';

type Props = {
  venues: VenueWithSharedMenuLinkInfo[] | null;
};

export type SortBy = 'name' | 'sharedMenuName' | 'address' | 'city';
export type SortingOrder = 'ASC' | 'DESC';
export type SortByState = Partial<Record<SortBy, SortingOrder>> | undefined;

export const useVenueList = ({ venues }: Props) => {
  const intl = useIntl();
  const [searchInput, setSearchInput] = useState('');
  const scope = useScopeContext();
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [appliedSortBy, setAppliedSortBy] = useState<SortByState>();
  const { selectedIds, selectMultiple, deselectMultiple, clearSelection } = useVenueSelection();

  const handleRowClick = useCallback(
    (clickedId: string) => {
      if (selectedIds.has(clickedId)) {
        deselectMultiple([clickedId]);
        return;
      }

      selectMultiple([clickedId]);
    },
    [deselectMultiple, selectMultiple, selectedIds],
  );

  const handleSearch = useCallback((input: string) => {
    setSearchInput(input);
  }, []);

  const handleSorting = useCallback(
    (sortingProperty: SortBy) => {
      const updatedSortBy: SortByState =
        appliedSortBy?.[sortingProperty] === 'ASC' ? { [sortingProperty]: 'DESC' } : { [sortingProperty]: 'ASC' };
      setAppliedSortBy(updatedSortBy);
    },
    [appliedSortBy],
  );

  const searchVenues = useCallback(
    (venuesToSearch: VenueWithSharedMenuLinkInfo[] | null) => {
      if (!searchInput || !venuesToSearch || !venuesToSearch.length) {
        return venuesToSearch;
      }

      return venuesToSearch.filter(venue =>
        localize(venue.name, intl.locale).toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
      );
    },
    [intl.locale, searchInput],
  );

  const filterVenues = useCallback(
    (venuesToFilter: VenueWithSharedMenuLinkInfo[] | null) => {
      if (!appliedFilters.length || !venuesToFilter || !venuesToFilter.length) {
        return venuesToFilter;
      }

      return venuesToFilter?.filter(venue =>
        appliedFilters.some(filter => venue.city.toLocaleLowerCase() === filter.toLocaleLowerCase()),
      );
    },
    [appliedFilters],
  );

  const sortVenues = useCallback(
    (venuesToSort: VenueWithSharedMenuLinkInfo[] | null) => {
      if (!appliedSortBy || !venuesToSort || !venuesToSort.length) {
        return venuesToSort;
      }

      const stringAccessors: Record<SortBy, (venue: VenueWithSharedMenuLinkInfo) => string> = {
        name: venue => localize(venue.name, intl.locale),
        sharedMenuName: venue => {
          if (venue.sharedMenuLink?.scope === scope.type && venue.sharedMenuLink.sharedMenuName) {
            return venue.sharedMenuLink.sharedMenuName;
          }

          return venue.sharedMenuLink?.scope === 'none'
            ? '-'
            : intl.formatMessage({ id: 'shared-menu.venue-list.other-scope' });
        },
        address: venue => venue.address,
        city: venue => venue.city,
      };

      const [currAppliedSortBy] = getTypedKeys(appliedSortBy);

      return R.sort((venueA, venueB) => {
        const stringToCompareA = stringAccessors[currAppliedSortBy](venueA);
        const stringToCompareB = stringAccessors[currAppliedSortBy](venueB);

        return appliedSortBy?.[currAppliedSortBy] === 'ASC'
          ? stringToCompareA.localeCompare(stringToCompareB)
          : stringToCompareB.localeCompare(stringToCompareA);
      }, venuesToSort);
    },
    [appliedSortBy, intl, scope.type],
  );

  const shownVenues = useMemo(
    () => sortVenues(filterVenues(searchVenues(venues))),
    [filterVenues, searchVenues, sortVenues, venues],
  );

  const uniqueCities = useMemo(() => {
    if (!venues || !venues.length) {
      return [];
    }

    return [...new Set(venues.map(venue => venue.city))];
  }, [venues]);

  const selectedVenues = useMemo(() => {
    return [...selectedIds].map(id => venues?.find(venue => venue.id === id)).filter(isDefined);
  }, [selectedIds, venues]);

  const selectedShownVenues = useMemo(() => {
    return (shownVenues ?? []).filter(venue => selectedIds.has(venue.id));
  }, [selectedIds, shownVenues]);

  const handleSelectAllShownVenues = useCallback(() => {
    if (shownVenues) {
      selectMultiple(shownVenues.map(venue => venue.id));
    }
  }, [selectMultiple, shownVenues]);

  const handleDeselectAllShownVenues = useCallback(() => {
    // If list is not filtered, clear the whole selection
    if (!searchInput) {
      clearSelection();
    } else {
      // If list is filtered, only deselect the ones that are in the filtered list (visible)
      deselectMultiple(selectedShownVenues.map(venue => venue.id));
    }
  }, [clearSelection, deselectMultiple, searchInput, selectedShownVenues]);

  useEffect(() => {
    if (venues && selectedIds) {
      const missingIds = [...selectedIds].filter(id => !venues.find(venue => venue.id === id));

      if (missingIds.length > 0) {
        deselectMultiple(missingIds);
      }
    }
  }, [deselectMultiple, selectedIds, venues]);

  return {
    selectedVenueIds: selectedIds,
    selectedVenues,
    selectedShownVenues,
    onRowClick: handleRowClick,
    selectAllShownVenues: handleSelectAllShownVenues,
    deselectAllShownVenues: handleDeselectAllShownVenues,
    shownVenues,
    searchInput,
    searchVenues: handleSearch,
    uniqueCities,
    setAppliedFilters,
    appliedFilters,
    handleSorting,
    appliedSortBy,
  };
};
